import { useCallback, useRef, useState } from "react";

/**
 * useMediaRecorder({ video }) — records audio (default) or video.
 * Returns { recording, error, elapsed (sec), preview (object url for live video stream),
 *           start, stop } where stop() returns a Blob (or null).
 */
export default function useMediaRecorder({ video = false } = {}) {
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [stream, setStream] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);

  const start = useCallback(async () => {
    setError(null);
    setElapsed(0);
    try {
      const constraints = video
        ? { audio: true, video: { width: 1280, height: 720, facingMode: "user" } }
        : { audio: true };
      const localStream = await navigator.mediaDevices.getUserMedia(constraints);

      // Codec selection: A/V SYNC is the priority here.
      //
      // Chrome says 'video/mp4' bare is supported but internally muxes VP9+Opus
      // into an MP4 container, which drifts because container & codec timestamps
      // don't align. We only accept MP4 if an explicit H.264+AAC string passes
      // isTypeSupported (Safari/iOS/some Chromium builds) — otherwise we use
      // native WebM (VP9+Opus), which Chrome muxes cleanly with synced A/V.
      const mp4Strict = video
        ? [
            'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
            "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
            "video/mp4;codecs=h264,aac",
          ]
        : ['audio/mp4;codecs="mp4a.40.2"', "audio/mp4;codecs=mp4a.40.2"];
      const webmCandidates = video
        ? ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]
        : ["audio/webm;codecs=opus", "audio/webm"];

      let mime = "";
      for (const c of mp4Strict) {
        if (MediaRecorder.isTypeSupported(c)) {
          mime = c;
          break;
        }
      }
      if (!mime) {
        for (const c of webmCandidates) {
          if (MediaRecorder.isTypeSupported(c)) {
            mime = c;
            break;
          }
        }
      }
      const recorderOpts = mime ? { mimeType: mime } : {};
      // Bitrate hints — strong enough for clear speech + portrait video without
      // being wasteful. MediaRecorder will round/cap as the platform needs.
      if (video) {
        recorderOpts.videoBitsPerSecond = 2_500_000; // ~2.5 Mbps 720p
        recorderOpts.audioBitsPerSecond = 128_000;
      } else {
        recorderOpts.audioBitsPerSecond = 128_000;
      }
      const mr = new MediaRecorder(localStream, recorderOpts);

      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      // No timeslice — a single `stop()`-triggered blob is correctly muxed with
      // synchronized A/V. Using start(250) forces many tiny fragments that can
      // drift when stitched back together.
      mr.start();
      mediaRef.current = { mr, stream: localStream };
      setStream(localStream);
      setRecording(true);

      const t0 = Date.now();
      tickRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - t0) / 1000));
      }, 500);
    } catch (e) {
      setError(e.message || "Could not access microphone/camera");
    }
  }, [video]);

  const stop = useCallback(
    () =>
      new Promise((resolve) => {
        const current = mediaRef.current;
        if (!current) {
          resolve(null);
          return;
        }
        const { mr, stream: s } = current;
        mr.onstop = () => {
          const type = mr.mimeType || (video ? "video/webm" : "audio/webm");
          const blob = new Blob(chunksRef.current, { type });
          s.getTracks().forEach((t) => t.stop());
          if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
          }
          mediaRef.current = null;
          setStream(null);
          setRecording(false);
          resolve(blob);
        };
        mr.stop();
      }),
    [video]
  );

  return { recording, error, elapsed, stream, start, stop };
}
